import React, { useState, useEffect } from 'react';
import axios from 'axios';
import OwnerPageLayout from './layout/OwnerPageLayout';
import PendingActionsPanel from './PendingActionsPanel';
import ToastStack from './ToastStack';
import useUndoActions from '../hooks/useUndoActions';
import { getSchoolForSession, getSchoolIdFromResponse } from '../utils/schoolHelper';
import { getApiBaseUrl } from '../apiBase';
import DemoViewBanner from '../demo/DemoViewBanner';
import { isViewingDemoSchool } from '../utils/activeSchool';
import { useTranslation } from 'react-i18next';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { getPaymentMethodDisplay } from '../utils/paymentMethodDisplay';


const API = getApiBaseUrl();

export default function SubscriptionManager() {
    const { t } = useTranslation(['subscriptionManager', 'common', 'nav', 'messages', 'errors']);
    const params = new URLSearchParams(window.location.search);
    const initId = params.get('studentId') || '';
    const initName = params.get('name') || '';

    const [students, setStudents] = useState([]);
    const [groups, setGroups] = useState([]);
    const [schoolName, setSchoolName] = useState('');
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [studentSearch, setStudentSearch] = useState('');
    const [studentId, setStudentId] = useState(initId);
    const [studentName, setStudentName] = useState(initName);
    const [subs, setSubs] = useState([]);
    const [balance, setBalance] = useState(null);
    const [history, setHistory] = useState([]);
    const [tab, setTab] = useState('subs');
    const [plans, setPlans] = useState([]);
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const { pendingActions, toasts, scheduleUndoableAction, cancelPendingAction, executePendingActionNow, dismissToast } = useUndoActions(10000);

    const [depForm, setDepForm] = useState({ amount: '', comment: '', paymentMethod: 'cash' });
    const isOwner = (localStorage.getItem('role') || '') === 'Owner';

    const token = localStorage.getItem('token');
    const api = axios.create({
        baseURL: API,
        headers: { Authorization: `Bearer ${token}` }
    });

    const inputStyle = {
        width: '100%', padding: '11px 14px', borderRadius: '10px',
        border: '1.5px solid var(--sp-gray-200)', fontSize: '14px',
        outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s',
        fontFamily: 'inherit'
    };

    useEffect(() => { loadStudents(); }, []);
    useEffect(() => { if (studentId) loadStudentData(); }, [studentId]);
    async function loadStudents() {
        try {
            const schoolRes = await getSchoolForSession(api);
            const schoolId = getSchoolIdFromResponse(schoolRes);
            setSchoolName(schoolRes.data.name || '');
            const studRes = await api.get(`/student?schoolId=${schoolId}`);
            const groupRes = await api.get(`/school/${schoolId}/groups`);
            setStudents(studRes.data);
            setGroups(groupRes.data || []);
            if (isViewingDemoSchool() && !initId && studRes.data?.length && !studentId) {
                const first = studRes.data[0];
                setStudentId(String(first.id));
                setStudentName(first.fullName || '');
            }
            try {
                const plansRes = await api.get(`/subscriptionplan?schoolId=${schoolId}`);
                setPlans(plansRes.data);
            } catch {
                setPlans([]);
            }
        } catch {
            setStudents([]);
            setGroups([]);
            setPlans([]);
        }
    }

    const groupFilteredStudents = selectedGroupId
        ? students.filter(s => s.activeGroupId === parseInt(selectedGroupId))
        : students;
    const filteredStudents = groupFilteredStudents.filter(s =>
        (s.fullName || '').toLowerCase().includes(studentSearch.toLowerCase()) ||
        (s.phone || s.parentPhone || '').includes(studentSearch)
    );

    async function loadStudentData() {
        try {
            const [subsRes, balRes] = await Promise.all([
                api.get(`/subscription?studentId=${studentId}`),
                api.get(`/subscription/balance?studentId=${studentId}`)
            ]);
            setSubs(subsRes.data);
            setBalance(balRes.data.balance);
            setHistory(balRes.data.history);
        } catch { alert(t('subscriptionManager:loadError')); }
    }

    async function handleBuySub(e) {
        e.preventDefault();
        if (!selectedPlanId) return alert(t('subscriptionManager:pickPlan'));
        try {
            await api.post('/subscription', {
                studentId: parseInt(studentId),
                subscriptionPlanId: parseInt(selectedPlanId)
            });
            setSelectedPlanId('');
            loadStudentData();
            alert(t('subscriptionManager:subAdded'));
        } catch (err) {
            alert(err.response?.data?.message || t('common:error'));
        }
    }

    async function handleDeposit(e) {
        e.preventDefault();
        try {
            await api.post('/subscription/deposit', {
                studentId: parseInt(studentId),
                amount: parseFloat(depForm.amount),
                paymentMethod: isOwner ? depForm.paymentMethod : 'cash',
                comment: depForm.comment || null
            });
            setDepForm({ amount: '', comment: '', paymentMethod: 'cash' });
            loadStudentData();
            alert(t('subscriptionManager:balanceToppedUp'));
        } catch (err) {
            alert(err.response?.data?.message || t('common:error'));
        }
    }

    async function handleDeduct(subId) {
        const sub = subs.find(x => x.id === subId);
        scheduleUndoableAction(t('subscriptionManager:deductUndo', { name: sub?.name || subId }), async () => {
            try {
                await api.post(`/subscription/${subId}/deduct`);
                loadStudentData();
            } catch (err) {
                alert(err.response?.data?.message || t('common:error'));
            }
        });
    }

    const TABS = [
        { key: 'subs', label: t('subscriptionManager:tabSubs') },
        { key: 'deposit', label: t('subscriptionManager:tabDeposit') },
        { key: 'history', label: t('subscriptionManager:tabHistory') },
    ];

    return (
        <>
            <ToastStack toasts={toasts} onDismiss={dismissToast} />
            <OwnerPageLayout
                title={t('subscriptionManager:title')}
                subtitle={t('subscriptionManager:subtitle')}
                maxWidth={960}
                beforeMain={<DemoViewBanner />}
            >
                <PendingActionsPanel
                    pendingActions={pendingActions}
                    onCancel={cancelPendingAction}
                    onExecuteNow={executePendingActionNow}
                />

                <div className="sp-panel">
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#555', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {t('subscriptionManager:filterLabel')}
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1.4fr', gap: '10px' }}>
                        <input
                            value={schoolName}
                            disabled
                            style={{ ...inputStyle, background: '#f8fafc', color: '#555' }}
                            placeholder={t('subscriptionManager:schoolPlaceholder')}
                        />
                        <select
                            value={selectedGroupId}
                            onChange={e => {
                                setSelectedGroupId(e.target.value);
                                setStudentId('');
                                setStudentName('');
                                setStudentSearch('');
                            }}
                            style={{ ...inputStyle, background: 'white' }}
                            onFocus={e => e.target.style.borderColor = '#EB3333'}
                            onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                        >
                            <option value="">{t('subscriptionManager:allGroups')}</option>
                            {groups.map(g => (
                                <option key={g.id} value={g.id}>{g.name}</option>
                            ))}
                        </select>
                        <input
                            value={studentSearch}
                            onChange={e => setStudentSearch(e.target.value)}
                            placeholder={t('subscriptionManager:searchStudent')}
                            style={{ ...inputStyle, gridColumn: '1 / 3' }}
                            onFocus={e => e.target.style.borderColor = '#EB3333'}
                            onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                        />
                        <select
                            value={studentId}
                            onChange={e => {
                                setStudentId(e.target.value);
                                const s = students.find(st => st.id === parseInt(e.target.value));
                                setStudentName(s?.fullName || '');
                            }}
                            style={{ ...inputStyle, background: 'white' }}
                            onFocus={e => e.target.style.borderColor = '#EB3333'}
                            onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                        >
                            <option value="">
                                {selectedGroupId ? t('subscriptionManager:pickFromGroup') : t('subscriptionManager:pickStudent')}
                            </option>
                            {filteredStudents.map(s => (
                                <option key={s.id} value={s.id}>{s.fullName}</option>
                            ))}
                        </select>
                    </div>
                    {studentSearch && (
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#888' }}>
                            {t('subscriptionManager:foundCount', { count: filteredStudents.length })}
                        </div>
                    )}
                </div>

                {!studentId ? (
                    <div style={{
                        textAlign: 'center', padding: '60px 20px',
                        background: 'white', borderRadius: '16px', border: '1px solid var(--sp-gray-100)',
                        color: '#aaa'
                    }}>
                        <div style={{ fontSize: '48px', marginBottom: '14px' }}>👆</div>
                        <div style={{ fontSize: '16px' }}>{t('subscriptionManager:pickStudentHint')}</div>
                    </div>
                ) : (
                    <>
                        {/* Баланс */}
                        {balance !== null && (
                            <div style={{
                                borderRadius: '14px', padding: '20px 24px', marginBottom: '20px',
                                background: balance >= 0
                                    ? 'linear-gradient(135deg, #e8f5e9, #f0fff4)'
                                    : 'linear-gradient(135deg, #fce4ec, #fff5f5)',
                                border: `1px solid ${balance >= 0 ? '#c8e6c9' : '#ffcdd2'}`,
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                            }}>
                                <div>
                                    <div style={{ fontSize: '13px', color: '#888', marginBottom: '4px' }}>
                                        {t('subscriptionManager:balanceLabel', { name: studentName })}
                                    </div>
                                    <div style={{
                                        fontSize: '32px', fontWeight: '800',
                                        color: balance >= 0 ? '#2e7d32' : '#c62828'
                                    }}>
                                        {balance >= 0 ? '+' : ''}{balance.toLocaleString('ru-RU')} ₽
                                    </div>
                                </div>
                                <div style={{ fontSize: '48px', opacity: 0.4 }}>
                                    {balance >= 0 ? '💚' : '🔴'}
                                </div>
                            </div>
                        )}

                        {/* Вкладки */}
                        <div style={{
                            display: 'flex', borderBottom: '2px solid var(--sp-gray-100)',
                            marginBottom: '20px', gap: '4px'
                        }}>
                            {TABS.map(tabItem => (
                                <button key={tabItem.key} onClick={() => setTab(tabItem.key)} style={{
                                    padding: '12px 20px', border: 'none', background: 'none',
                                    cursor: 'pointer', fontSize: '14px', fontWeight: tab === tabItem.key ? '700' : '400',
                                    color: tab === tabItem.key ? '#1A1A1A' : '#888',
                                    borderBottom: `2px solid ${tab === tabItem.key ? '#EB3333' : 'transparent'}`,
                                    marginBottom: '-2px', transition: 'all 0.15s'
                                }}>
                                    {tabItem.label}
                                </button>
                            ))}
                        </div>

                        {/* ВКЛАДКА: АБОНЕМЕНТЫ */}
                        {tab === 'subs' && (
                            <div>
                                {/* Активные абонементы */}
                                {subs.length === 0 ? (
                                    <div style={{
                                        textAlign: 'center', padding: '32px',
                                        background: 'white', borderRadius: '14px',
                                        border: '1px solid var(--sp-gray-100)', color: '#aaa',
                                        marginBottom: '20px', fontSize: '14px'
                                    }}>
                                        <div style={{ fontSize: '36px', marginBottom: '10px' }}>🎫</div>
                                        {t('subscriptionManager:noSubs')}
                                    </div>
                                ) : (
                                    <div style={{ display: 'grid', gap: '10px', marginBottom: '24px' }}>
                                        {subs.map(s => (
                                            <div key={s.id} style={{
                                                background: 'white', borderRadius: '14px', padding: '18px 20px',
                                                border: `1px solid ${s.isActive ? '#c8e6c9' : '#eee'}`,
                                                opacity: s.isActive ? 1 : 0.6
                                            }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontWeight: '700', fontSize: '16px', marginBottom: '4px' }}>
                                                            {s.name}
                                                        </div>
                                                        <div style={{ fontSize: '13px', color: '#888' }}>
                                                            {t('subscriptionManager:purchased', { date: formatDate(s.purchaseDate) })}
                                                            {s.expiryDate && (
                                                                <span style={{ marginLeft: '14px' }}>
                                                                    {t('subscriptionManager:until', { date: formatDate(s.expiryDate) })}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div style={{ fontSize: '13px', color: '#555', marginTop: '4px' }}>
                                                            {t('subscriptionManager:paid', { amount: s.pricePaid?.toLocaleString('ru-RU') })}
                                                        </div>
                                                    </div>
                                                    <div style={{ textAlign: 'right' }}>
                                                        {s.totalLessons != null ? (
                                                            <>
                                                                <div style={{
                                                                    fontSize: '26px', fontWeight: '800',
                                                                    color: (s.lessonsLeft || 0) > 2 ? '#2e7d32' : '#e65100'
                                                                }}>
                                                                    {s.lessonsLeft} / {s.totalLessons}
                                                                </div>
                                                                <div style={{ fontSize: '12px', color: '#aaa' }}>{t('subscriptionManager:lessons')}</div>
                                                            </>
                                                        ) : (
                                                            <div style={{ fontSize: '22px', fontWeight: '800', color: '#EB3333' }}>♾️</div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Прогресс бар */}
                                                {s.totalLessons != null && (
                                                    <div style={{ height: '6px', background: '#f0f0f0', borderRadius: '3px', margin: '12px 0' }}>
                                                        <div style={{
                                                            height: '100%', borderRadius: '3px',
                                                            width: `${((s.lessonsLeft || 0) / s.totalLessons) * 100}%`,
                                                            background: (s.lessonsLeft || 0) > 2 ? '#2ecc71' : '#e74c3c',
                                                            transition: 'width 0.3s'
                                                        }} />
                                                    </div>
                                                )}

                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <span style={{
                                                        background: s.isActive ? '#e8f5e9' : '#f5f5f5',
                                                        color: s.isActive ? '#2e7d32' : '#888',
                                                        padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '700'
                                                    }}>
                                                        {s.isActive ? t('subscriptionManager:active') : t('subscriptionManager:ended')}
                                                    </span>
                                                    {s.isActive && (
                                                        <button onClick={() => handleDeduct(s.id)} style={{
                                                            padding: '7px 16px', borderRadius: '8px',
                                                            background: '#fff3e0', color: '#e65100',
                                                            border: '1px solid #ffe0b2', cursor: 'pointer',
                                                            fontSize: '13px', fontWeight: '600'
                                                        }}>
                                                            {t('subscriptionManager:deductLesson')}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Купить абонемент по тарифу */}
                                <div style={{
                                    background: 'white', borderRadius: '14px', padding: '20px',
                                    border: '1px solid var(--sp-gray-100)'
                                }}>
                                    <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '700' }}>
                                        {t('subscriptionManager:buySub')}
                                    </h3>
                                    {plans.length === 0 ? (
                                        <div style={{ color: '#aaa', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                                            <div style={{ fontSize: '28px', marginBottom: '8px' }}>📋</div>
                                            {t('subscriptionManager:noPlansHint')}
                                        </div>
                                    ) : (
                                        <form onSubmit={handleBuySub}>
                                            <div style={{ display: 'grid', gap: '10px', marginBottom: '16px' }}>
                                                {plans.map(p => (
                                                    <div
                                                        key={p.id}
                                                        onClick={() => setSelectedPlanId(p.id.toString())}
                                                        style={{
                                                            padding: '14px 16px', borderRadius: '10px', cursor: 'pointer',
                                                            border: `2px solid ${selectedPlanId === p.id.toString() ? '#EB3333' : 'var(--sp-gray-200)'}`,
                                                            background: selectedPlanId === p.id.toString() ? '#f0ebff' : 'white',
                                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                            transition: 'all 0.15s'
                                                        }}
                                                    >
                                                        <div>
                                                            <div style={{ fontWeight: '700', fontSize: '15px' }}>{p.name}</div>
                                                            <div style={{ fontSize: '13px', color: '#888', marginTop: '2px' }}>
                                                                {p.lessonsCount ? t('subscriptionManager:lessonsCount', { count: p.lessonsCount }) : t('subscriptionManager:unlimited')}
                                                                {p.validDays ? t('subscriptionManager:validDays', { days: p.validDays }) : ''}
                                                            </div>
                                                        </div>
                                                        <div style={{
                                                            fontSize: '20px', fontWeight: '800',
                                                            color: selectedPlanId === p.id.toString() ? '#EB3333' : '#1A1A1A'
                                                        }}>
                                                            {p.currentPrice?.toLocaleString('ru-RU')} ₽
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            <button type="submit" disabled={!selectedPlanId} style={{
                                                padding: '12px 28px', background: selectedPlanId ? '#1A1A1A' : '#94a3b8',
                                                color: 'white', border: 'none', borderRadius: '10px',
                                                fontSize: '15px', fontWeight: '700',
                                                cursor: selectedPlanId ? 'pointer' : 'not-allowed'
                                            }}>
                                                {t('subscriptionManager:buySub')}
                                            </button>
                                        </form>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ВКЛАДКА: ПОПОЛНЕНИЕ */}
                        {tab === 'deposit' && (
                            <div style={{
                                background: 'white', borderRadius: '14px',
                                padding: '24px', border: '1px solid var(--sp-gray-100)'
                            }}>
                                <h3 style={{ margin: '0 0 20px', fontSize: '17px', fontWeight: '700' }}>
                                    {t('subscriptionManager:topUpTitle')}
                                </h3>
                                <form onSubmit={handleDeposit}>
                                    {isOwner && (
                                        <div className="payment-method-selector" style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                                                <input
                                                    type="radio"
                                                    value="cash"
                                                    checked={depForm.paymentMethod === 'cash'}
                                                    onChange={() => setDepForm({ ...depForm, paymentMethod: 'cash' })}
                                                />
                                                💵 {t('common:payment.cash')}
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                                                <input
                                                    type="radio"
                                                    value="card_transfer"
                                                    checked={depForm.paymentMethod === 'card_transfer'}
                                                    onChange={() => setDepForm({ ...depForm, paymentMethod: 'card_transfer' })}
                                                />
                                                💳 {t('common:payment.cardTransfer')}
                                            </label>
                                        </div>
                                    )}
                                    <div style={{ marginBottom: '16px' }}>
                                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
                                            {t('subscriptionManager:amountLabel')}
                                        </label>
                                        <input
                                            type="number" min="1"
                                            value={depForm.amount}
                                            onChange={e => setDepForm({ ...depForm, amount: e.target.value })}
                                            placeholder="5000"
                                            required style={inputStyle}
                                            onFocus={e => e.target.style.borderColor = '#EB3333'}
                                            onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                        />
                                    </div>
                                    <div style={{ marginBottom: '24px' }}>
                                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
                                            {t('common:payment.comment')}
                                        </label>
                                        <input
                                            value={depForm.comment}
                                            onChange={e => setDepForm({ ...depForm, comment: e.target.value })}
                                            placeholder={t('subscriptionManager:commentPlaceholder')}
                                            style={inputStyle}
                                            onFocus={e => e.target.style.borderColor = '#EB3333'}
                                            onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                        />
                                    </div>
                                    <button type="submit" style={{
                                        padding: '13px 32px', background: '#1A1A1A', color: 'white',
                                        border: 'none', borderRadius: '10px', fontSize: '15px',
                                        fontWeight: '700', cursor: 'pointer'
                                    }}>
                                        {t('subscriptionManager:topUpBtn')}
                                    </button>
                                </form>
                            </div>
                        )}

                        {/* ВКЛАДКА: ИСТОРИЯ */}
                        {tab === 'history' && (
                            <div style={{
                                background: 'white', borderRadius: '14px',
                                padding: '24px', border: '1px solid var(--sp-gray-100)'
                            }}>
                                <h3 style={{ margin: '0 0 20px', fontSize: '17px', fontWeight: '700' }}>
                                    {t('subscriptionManager:historyTitle')}
                                </h3>
                                {history.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '32px', color: '#aaa' }}>
                                        <div style={{ fontSize: '36px', marginBottom: '10px' }}>📋</div>
                                        {t('subscriptionManager:noHistory')}
                                    </div>
                                ) : (
                                    history.map(tx => {
                                        const pay = getPaymentMethodDisplay(tx.paymentMethod, t);
                                        return (
                                        <div key={tx.id} style={{
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            padding: '12px 0', borderBottom: '1px solid #f5f5f5'
                                        }}>
                                            <div>
                                                <div style={{ fontSize: '14px', fontWeight: '600', color: '#1A1A1A' }}>
                                                    {tx.description}
                                                </div>
                                                <div style={{ fontSize: '12px', color: '#aaa', marginTop: '2px' }}>
                                                    {formatDateTime(tx.date, {
                                                        day: 'numeric', month: 'long',
                                                        hour: '2-digit', minute: '2-digit'
                                                    })}
                                                    {pay && (
                                                        <span style={{ marginLeft: '10px' }}>
                                                            {pay.icon} {pay.label}
                                                        </span>
                                                    )}
                                                    {tx.isManual && tx.createdByName && (
                                                        <span style={{ marginLeft: '10px' }}>
                                                            · {t('common:payment.addedBy')}: {tx.createdByName}
                                                        </span>
                                                    )}
                                                    {tx.notes && (
                                                        <span style={{ marginLeft: '10px' }}>
                                                            · {tx.notes}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div style={{
                                                fontWeight: '800', fontSize: '16px',
                                                color: tx.amount > 0 ? '#2e7d32' : tx.amount < 0 ? '#c62828' : '#888'
                                            }}>
                                                {tx.amount > 0 ? '+' : ''}{tx.amount.toLocaleString('ru-RU')} ₽
                                            </div>
                                        </div>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </>
                )}
            </OwnerPageLayout>
        </>
    );
}